import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatDateTimeIT } from "@/lib/formatters";
import { backendUrl } from "@/lib/backendUrl";

const ENTRY_CONFIG = {
  confirm: {
    previewPath: "confirm",
    actionPath: "confirm",
    idleTitle: "Conferma appuntamento",
    idleMessage: "Controlla i dettagli e conferma la tua presenza.",
    actionLabel: "Conferma appuntamento",
    loadingMessage: "Stiamo caricando i dettagli dell'appuntamento.",
    actionLoadingMessage: "Stiamo registrando la tua conferma.",
  },
  cancel: {
    previewPath: "cancel",
    actionPath: "cancel",
    idleTitle: "Cancella appuntamento",
    idleMessage: "Controlla i dettagli prima di confermare la cancellazione.",
    actionLabel: "Conferma cancellazione",
    loadingMessage: "Stiamo caricando i dettagli dell'appuntamento.",
    actionLoadingMessage: "Stiamo registrando la cancellazione.",
  },
};

const ACTION_RESULT_STATES = {
  confirm: {
    success: {
      title: "Appuntamento confermato",
      message: "La tua conferma è stata registrata correttamente.",
      tone: "success",
    },
    already_confirmed: {
      title: "Appuntamento già confermato",
      message: "Questa conferma risultava già presente.",
      tone: "success",
    },
    unavailable: {
      title: "Link non disponibile",
      message: "Non è più possibile confermare questo appuntamento da questo link.",
      tone: "warning",
    },
  },
  cancel: {
    success: {
      title: "Appuntamento cancellato",
      message: "La cancellazione è stata registrata correttamente.",
      tone: "success",
    },
    already_cancelled: {
      title: "Appuntamento già cancellato",
      message: "Questa cancellazione risultava già presente.",
      tone: "success",
    },
    unavailable: {
      title: "Link non disponibile",
      message: "Non è più possibile cancellare questo appuntamento da questo link.",
      tone: "warning",
    },
  },
};

function formatValue(value, fallback = "Non disponibile") {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }
  return String(value);
}

function mapActionResult(entryAction, status, payload) {
  const message = typeof payload?.message === "string" ? payload.message : "";

  if (status === 404) {
    return ACTION_RESULT_STATES[entryAction].unavailable;
  }

  if (entryAction === "confirm") {
    if (status === 200 && message === "Appointment confirmed via reminder.") {
      return ACTION_RESULT_STATES.confirm.success;
    }
    if (status === 200 && message === "Appointment already confirmed via this reminder.") {
      return ACTION_RESULT_STATES.confirm.already_confirmed;
    }
  }

  if (entryAction === "cancel") {
    if (status === 200 && message === "Appointment cancelled. Recovery engine activated.") {
      return ACTION_RESULT_STATES.cancel.success;
    }
    if (status === 200 && message === "Appointment already cancelled via this reminder.") {
      return ACTION_RESULT_STATES.cancel.already_cancelled;
    }
  }

  return ACTION_RESULT_STATES[entryAction].unavailable;
}

function CustomerReminderPageContent({ entryAction }) {
  const { token } = useParams();
  const config = ENTRY_CONFIG[entryAction];
  const [preview, setPreview] = useState(null);
  const [pageState, setPageState] = useState(token ? "loading" : "error");
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let active = true;

    const loadPreview = async () => {
      if (!token) {
        setPageState("error");
        return;
      }

      setPageState("loading");
      try {
        const response = await fetch(
          `${backendUrl}/api/public/reminders/${config.previewPath}/${encodeURIComponent(token)}/preview`
        );
        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!active) {
          return;
        }

        if (response.status === 404 || !payload?.appointment) {
          setPageState("invalid");
          return;
        }

        setPreview(payload);
        setPageState("ready");
      } catch (error) {
        if (active) {
          setPageState("error");
        }
      }
    };

    loadPreview();
    return () => {
      active = false;
    };
  }, [config.previewPath, token]);

  const submitAction = async () => {
    if (!token || pageState === "submitting") {
      return;
    }

    setPageState("submitting");
    try {
      const response = await fetch(
        `${backendUrl}/api/public/reminders/${config.actionPath}/${encodeURIComponent(token)}/action`,
        { method: "POST" }
      );
      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      setFeedback(mapActionResult(entryAction, response.status, payload));
      setPageState("result");
    } catch (error) {
      setPageState("error");
    }
  };

  const statusTone = useMemo(() => {
    if (feedback?.tone === "success") {
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    }
    if (pageState === "ready" && preview?.action_available) {
      return "border-slate-200 bg-slate-50 text-slate-700";
    }
    return "border-amber-200 bg-amber-50 text-amber-700";
  }, [feedback, pageState, preview]);

  const appointment = preview?.appointment;

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 flex items-center justify-center">
      <section className="w-full max-w-xl bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">FullBook Customer App</p>
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            {entryAction === "confirm" ? "Vista appuntamento" : "Vista cancellazione"}
          </p>
        </div>

        <div className={`mb-5 flex h-11 w-11 items-center justify-center rounded-full border ${statusTone}`}>
          {pageState === "loading" || pageState === "submitting" ? "..." : feedback?.tone === "success" ? "OK" : "!"}
        </div>

        {(pageState === "loading" || pageState === "submitting") && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">{config.idleTitle}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {pageState === "loading" ? config.loadingMessage : config.actionLoadingMessage}
            </p>
          </>
        )}

        {pageState === "invalid" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Link non valido o scaduto</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Non è possibile aprire questo appuntamento da questo link.
            </p>
          </>
        )}

        {pageState === "error" && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">Errore temporaneo</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Non siamo riusciti a caricare questa pagina. Riprova più tardi.
            </p>
          </>
        )}

        {pageState === "result" && feedback && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">{feedback.title}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{feedback.message}</p>
          </>
        )}

        {pageState === "ready" && appointment && (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 section-title">{config.idleTitle}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {preview?.status_message || config.idleMessage}
            </p>

            <div className="mt-6 rounded-sm border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Servizio</p>
                  <p className="mt-1 text-slate-900">{formatValue(appointment.service_name)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Stato</p>
                  <p className="mt-1 text-slate-900">{formatValue(appointment.status)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Inizio</p>
                  <p className="mt-1 text-slate-900">{formatDateTimeIT(appointment.start_time)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Fine</p>
                  <p className="mt-1 text-slate-900">{formatDateTimeIT(appointment.end_time)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Staff</p>
                  <p className="mt-1 text-slate-900">{formatValue(appointment.staff_name)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Origine</p>
                  <p className="mt-1 text-slate-900">{formatValue(appointment.origin)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={submitAction}
                disabled={!preview.action_available}
                className="h-11 flex-1 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50"
              >
                {config.actionLabel}
              </button>
              <button
                type="button"
                onClick={() => window.close()}
                className="h-11 flex-1 rounded-sm border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700"
              >
                Chiudi pagina
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export function CustomerReminderConfirmPage() {
  return <CustomerReminderPageContent entryAction="confirm" />;
}

export function CustomerReminderCancelPage() {
  return <CustomerReminderPageContent entryAction="cancel" />;
}
